/** Gedeelde weergave voor het Beroepsregistratiecertificaat (cockpitkaart + publieke verificatiepagina). */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ComplianceRegel } from '../services/beroepsregistratieApi';
import { fmtDag, fmtGetal, kort } from '../utils/beroepsregistratieFormat';

const PIJLER_KLEUR: Record<ComplianceRegel['pijler'], string> = {
  PE_PUNTEN: 'bg-[#E5A425]/15 text-[#8a5d06]',
  INTERVISIE: 'bg-blue-50 text-[#0B1F3A]',
  PRAKTIJKUREN: 'bg-emerald-50 text-emerald-700',
};

/** Bewijsregels van de ledger. `compact` = cockpit (afgekapte ID's), anders volledige waarden (auditor). */
export const RegelsTabel: React.FC<{ regels: ComplianceRegel[]; compact?: boolean }> = ({ regels, compact = false }) => {
  const { t } = useTranslation('pages');
  const cel = compact ? 'px-3 py-2' : 'px-3 py-2.5';
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-100">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
          <tr>
            <th scope="col" className={cel}>{t('beroepsregistratie.table.date')}</th>
            <th scope="col" className={cel}>{t('beroepsregistratie.table.pillar')}</th>
            <th scope="col" className={cel}>{t('beroepsregistratie.table.description')}</th>
            <th scope="col" className={`${cel} text-right`}>{t('beroepsregistratie.table.amount')}</th>
            <th scope="col" className={cel}>{t('beroepsregistratie.table.evidence')}</th>
            {!compact && <th scope="col" className={cel}>{t('beroepsregistratie.table.rowHash')}</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-gray-700">
          {regels.map((r) => (
            <tr key={r.ledger_id} className="align-top">
              <td className={`${cel} whitespace-nowrap`}>{fmtDag(r.datum)}</td>
              <td className={cel}>
                <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${PIJLER_KLEUR[r.pijler] ?? 'bg-gray-100 text-gray-600'}`}>
                  {t(`beroepsregistratie.pillar.${r.pijler}`, { defaultValue: r.pijler })}
                </span>
                {r.status && (
                  <span className="mt-1 block text-[10px] text-gray-400">{t(`beroepsregistratie.status.${r.status}`, { defaultValue: r.status })}</span>
                )}
              </td>
              <td className={`${cel} min-w-[10rem]`}>{r.omschrijving}</td>
              <td className={`${cel} whitespace-nowrap text-right font-semibold text-[#0B1F3A]`}>
                {r.pe_punten != null
                  ? t('beroepsregistratie.points', { n: fmtGetal(r.pe_punten) })
                  : r.uren != null
                    ? t('beroepsregistratie.hours', { n: fmtGetal(r.uren) })
                    : '—'}
              </td>
              <td className={`${cel} min-w-[9rem] space-y-0.5`}>
                {r.accreditatie_register && (
                  <div>
                    <span className="text-gray-400">{t('beroepsregistratie.evidence.register')} </span>
                    {r.accreditatie_register}
                  </div>
                )}
                {r.meeting_id && (
                  <div className={compact ? '' : 'break-all'}>
                    <span className="text-gray-400">{t('beroepsregistratie.evidence.meeting')} </span>
                    <span className="font-mono text-[10px]" title={r.meeting_id}>{compact ? kort(r.meeting_id) : r.meeting_id}</span>
                  </div>
                )}
                {r.aanwezigheid_pct != null && (
                  <div>
                    <span className="text-gray-400">{t('beroepsregistratie.evidence.attendance')} </span>
                    {t('beroepsregistratie.evidence.pct', { n: fmtGetal(r.aanwezigheid_pct) })}
                  </div>
                )}
                {r.stripe_referentie && (
                  <div className={compact ? '' : 'break-all'}>
                    <span className="text-gray-400">{t('beroepsregistratie.evidence.stripe')} </span>
                    <span className="font-mono text-[10px]" title={r.stripe_referentie}>{compact ? kort(r.stripe_referentie) : r.stripe_referentie}</span>
                  </div>
                )}
                {r.factuurnummer && (
                  <div>
                    <span className="text-gray-400">{t('beroepsregistratie.evidence.invoice')} </span>
                    <span className="font-mono text-[10px]">{r.factuurnummer}</span>
                  </div>
                )}
                {!r.accreditatie_register && !r.meeting_id && !r.stripe_referentie && !r.factuurnummer && <span className="text-gray-400">—</span>}
              </td>
              {!compact && (
                <td className={`${cel} min-w-[8rem] break-all font-mono text-[10px] text-gray-400`}>{r.regel_hash}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
